export const settingsEn = {
  title: 'Settings',
  back: 'Back',
  save: 'Save Preferences',
  saveSuccess: 'Preferences saved to your account',
  saveError: 'Could not save preferences to the server. Changes are kept on this device.',
  loadError: 'Could not load preferences from the server. Showing local values.',
  lastUpdated: (iso: string) => `Last updated: ${new Date(iso).toLocaleString()}`,

  account: {
    title: 'Account',
    signedInAs: 'Signed in as',
    email: 'Email',
    name: 'Name',
    signOut: 'Sign out',
    userProfile: 'User Profile',
    userProfileNeedsCommunity:
      'Enable Community Features below to edit your User Profile.',
    inboundAddress: 'Inbound email',
    inboundCopy: 'Copy',
    inboundCopied: 'Inbound address copied',
    inboundNotProvisioned: 'Inbound email is not available for this account yet.',
    inboundLoadFailed: 'Could not load inbound email address.',
  },

  display: {
    title: 'Display Preferences',
    hidePastTrips: 'Hide Past Trips',
    hidePastCities: 'Hide Past Cities',
    hidePastDays: 'Hide Past Days',
    expandAllItemsByDefault: 'Expand All Items by Default',
    collapsePastItems: 'Collapse Past Items',
    hideCompletedOptions: 'Hide Completed Options',
    showPlanIcons: 'Show Plan Icons',
    reservationWarnings: 'Reservation Warnings',
    cruiseMealPlanning: 'Cruise Meal Planning',
    reservationRequired: 'Required',
    reservationRecommended: 'Recommended',
    reservationNone: 'No Warnings',
    cruiseOneMeal: '1 meal',
    cruiseThreeMeals: '3 meals',
  },

  userSettings: {
    title: 'User Settings',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Select nationality',
    homeTimeZone: 'Home Time Zone',
    homeTimeZonePlaceholder: 'Select time zone',
    preferredMapApp: 'Map App',
    mapApple: 'Apple Maps',
    mapGoogle: 'Google Maps',
    mapWaze: 'Waze',
    travelerPrefs: "Traveler's AI Preferences",
    checklistTemplate: 'Default Trip Checklist',
    allowedSenders: 'Allowed Sender Addresses',
  },

  community: {
    title: 'Community',
    enable: 'Enable Community Features',
    enableHint: 'Required to edit your User Profile and use community features.',
  },

  about: {
    title: 'About',
    version: 'Version',
    syncNow: 'Sync now',
    syncing: 'Syncing…',
    syncSuccess: 'Synced successfully',
    syncFailed: 'Sync failed. Check your connection and try again.',
    lastSync: 'Last sync',
    signInToSync: 'Sign in to sync',
    pendingPreferences:
      'Preferences have not synced yet. Tap Sync now to retry.',
  },

  legal: {
    title: 'Legal',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    attributions: 'Data attributions',
    openMeteo: 'Weather data by Open-Meteo',
    openMeteoUrl: 'https://open-meteo.com',
    googleMaps: 'Maps powered by Google Maps Platform',
    googleMapsUrl: 'https://maps.google.com/help/terms_maps/',
  },

  userProfile: {
    title: 'User Profile',
    editTitle: 'Edit Profile',
    createTitle: 'Create Profile',
    save: 'Save',
    saveSuccess: 'Profile saved',
    saveFailed: 'Could not save profile. Try again.',
    loadFailed: 'Could not load profile.',
    communityRequired: 'Community Features are turned off.',
    communityRequiredHint:
      'Turn on Enable Community Features in Settings to create or edit your profile.',
    openSettings: 'Back to Settings',
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    travelStyleHint:
      "Travel style tags are managed in Settings → Traveler's AI Preferences.",
    aboutYou: 'About You',
    bio: 'Bio',
    bioPlaceholder: 'Share a short introduction',
    homeCity: 'Home City',
    homeCityHint: 'Shown when available from your account; not yet editable on web.',
    ageRange: 'Age Range',
    ageNotSpecified: 'Not Specified',
    communityFeatures: 'Community Features',
    shareTrips: 'Share Trips',
    shareTripsHint: 'Allow connections to see your trips',
    shareLocation: 'Share Location',
    shareLocationHint: 'Enable real-time location sharing during trips',
    allowDiscovery: 'Allow Discovery',
    allowDiscoveryHint: 'Let other travelers find you in the same destination',
    profileVisibility: 'Profile Visibility',
    showProfileTo: 'Show Profile To',
    visibilityEveryone: 'Everyone',
    visibilityConnections: 'Connections Only',
    visibilityNoOne: 'No One',
    locationSharing: 'Location Sharing',
    locationSharingEnable: 'Enable Location Sharing',
    locationSharingHint: 'Allow your connections to see your location during trips',
    locationVisibility: 'Location Visibility',
    locationVisibilityHint: "Who can see your location when you're sharing",
    security: 'Security',
    changePassword: 'Change Password',
    changePasswordHint: 'Opens Cognito account recovery to set a new password.',
    blockedUsers: 'Blocked Users',
    blockedComingSoon: 'Manage Blocked Users (coming soon)',
    nameRequired: 'First and last name are required.',
  },

  allowedSenders: {
    title: 'Allowed Sender Addresses',
    explainer: (appName: string, inbound: string) =>
      `Email addresses on this list can send confirmations and attachments to your ${appName} account using the address ${inbound}. Any accounts that attempt to send info that are not on this list will be rejected.`,
    explainerNoAddress: (appName: string) =>
      `Email addresses on this list can send confirmations and attachments to your ${appName} account. Any accounts that attempt to send info that are not on this list will be rejected.`,
    accountEmail: 'Account email',
    section: 'Allowed Sender Addresses',
    empty: 'No alternate senders added yet.',
    addPlaceholder: 'Add email address',
    add: 'Add',
    remove: 'Remove',
    invalidEmail: 'Enter a valid email address.',
    duplicateEmail: 'That address is already on your list.',
    accountAlreadyAllowed: 'Your account email is already an allowed sender.',
    loadFailed: 'Could not load allowed senders.',
    notProvisioned: 'Allowed senders are not available for this account yet.',
    addFailed: 'Could not add that sender. Try again.',
    removeFailed: 'Could not remove that sender. Try again.',
    appName: 'Portcierge',
    openInbox: 'Open confirmations inbox',
  },

  travelerPrefs: {
    title: "Traveler's AI Preferences",
    subtitle:
      'These preferences guide AI trip planning. Saved to your account preferences.',
    travelStyle: 'Travel Style',
    travelStyleTags: 'Travel Style Tags',
    travelStyleTagsHint: 'Select all that apply',
    preferredTimeSlots: 'Preferred Time Slots',
    typicalStayDuration: (nights: number) =>
      `Typical Stay Duration: ${nights} night${nights === 1 ? '' : 's'}`,
    additionalPreferences: 'Additional Preferences',
    additionalPlaceholder: 'Anything else the planner should know…',
    yourPreferences: 'Your Preferences',
    accommodationType: 'Accommodation Type',
    accommodationPlaceholder: 'Select preferred accommodation type…',
    accommodationBudget: (amount: number) =>
      amount >= 750
        ? 'Average Accommodation Budget: $750+'
        : `Average Accommodation Budget: $${amount}`,
    sightseeingStyle: 'Sightseeing Style',
    sightseeingNoPreference: 'No preference',
    sightseeingIndependent: 'Independent',
    sightseeingSelfGuided: 'Self-guided',
    sightseeingSmallGroup: 'Small group',
    sightseeingLargeGroup: 'Large group',
    sightseeingPrivateGuide: 'Private guide',
    activityInterests: 'Activity Interests',
    activityInterestsHint: 'Select 3–5 topics (or pick specific interests inside a topic).',
    activitySelectedCount: (n: number) => `${n} selected`,
    activityPickItems: 'Pick items',
    activityValidationMin: 'Select at least 3 interests',
    activityValidationMax: 'Select at most 5 interests',
    dietaryPreferences: 'Dietary Preferences',
    cuisinePreferences: 'Cuisine & Food Preferences',
    cuisineLove: 'Love',
    cuisineLike: 'Like',
    cuisineAvoid: 'Avoid',
    extractedPatterns: 'AI-Detected Patterns',
    paceRelaxed: 'Relaxed',
    paceModerate: 'Moderate',
    paceFast: 'Fast-Paced',
    saveSuccess: 'Preferences saved',
  },

  checklist: {
    title: 'Default Trip Checklist',
    help: 'Applied automatically to new trips. Changes do not affect existing trip checklists.',
    items: 'Template Items',
    empty: 'No template items yet.',
    addPlaceholder: 'Add template item',
    add: 'Add',
    remove: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    resetDefaults: 'Reset to defaults',
  },
} as const;
